import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Camera, UserCog } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { ApiError, api } from '@/lib/api';
import { initials } from '@/lib/format';
import { useT } from '@/i18n/context';
import { ProtectedImage } from '@/components/media';
import { ImageCropDialog } from '@/components/ImageCropDialog';
import { CROP_SPECS } from '@/components/crop-specs';
import { useToast } from '@/components/ui';

/**
 * صورةُ الحساب (0049) في كل موضعٍ تُعرض فيه: `hasPhoto` من `/auth/me` تقول إن
 * `GET /me/photo` سيعيد صورةً فعلاً — فلا يُطلق طلبٌ يُردّ 404 على كل صفحة.
 * وبلا صورةٍ تُعرض الحروفُ الأولى كما كانت.
 */
export function MyAvatar({ size = 40, className = '' }: { size?: number; className?: string }) {
  const { me } = useAuth();
  if (!me) return null;
  if (!me.user.hasPhoto) {
    return <span className={`avatar ${className}`.trim()} style={{ inlineSize: size, blockSize: size, fontSize: size >= 72 ? 'var(--fs-h2)' : undefined }} aria-hidden>{initials(me.user.fullName)}</span>;
  }
  return <ProtectedImage path="/me/photo" alt="" size={size} rounded className={`avatar-img ${className}`.trim()} />;
}

/** زرُّ تغيير الصورة: يرفع الملفَ ثم يعيد تحميل الهوية ويُسقط نسخة الصورة من الذاكرة */
export function ChangePhotoButton({ compact = false }: { compact?: boolean }) {
  const { reload } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [cropping, setCropping] = useState<File | null>(null);

  async function onFile(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    try {
      await api.upload('/me/photo', file, {}, { institutionId: null });
      await qc.invalidateQueries({ queryKey: ['blob', '/me/photo'] });
      await reload();
      toast.push('success', t('hero.photoSaved'));
    } catch (e) {
      toast.push('error', e instanceof ApiError ? e.message : t('hero.photoFailed'));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  }

  return (
    <>
      <input ref={input} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) setCropping(f); }} />
      {cropping && <ImageCropDialog file={cropping} spec={CROP_SPECS.avatar} onCancel={() => setCropping(null)} onDone={(f) => { setCropping(null); void onFile(f); }} />}
      <button type="button" className={compact ? 'hero__cam' : 'btn btn--secondary btn--sm'} disabled={busy}
        onClick={() => input.current?.click()} aria-label={t('hero.changePhoto')} title={t('hero.changePhoto')}>
        <Camera size={compact ? 15 : 16} aria-hidden />
        {!compact && <span>{t('hero.changePhoto')}</span>}
      </button>
    </>
  );
}

/**
 * ترويسةُ الشخص في أول صفحةٍ بعد الدخول (22 سبتمبر 2026): وجهُه واسمُه وصفتُه
 * ومؤسستُه — ما كان في الصفحة الأولى العامة صار هنا لصاحبه. وتغييرُ الصورة من
 * الموضع نفسِه (زرُّ الكاميرا على الصورة)، وإعداداتُ الحساب رابطٌ لا أكثر.
 */
export function PersonHero({ subtitle, actions }: { subtitle?: string; actions?: React.ReactNode }) {
  const { me, roles, institutions, institutionId } = useAuth();
  const t = useT();
  if (!me || me.student) return null;
  const institution = institutions.find((i) => i.id === institutionId) ?? null;
  const roleText = roles.length ? roles.map((r) => t(`role.${r}`)).join(' · ') : '';
  const first = me.user.fullName.trim().split(/\s+/)[0] ?? me.user.fullName;
  return (
    <section className="hero" aria-label={t('hero.aria')}>
      <div className="hero__photo">
        <MyAvatar size={88} />
        <ChangePhotoButton compact />
      </div>
      <div className="hero__text">
        <h1 className="hero__name">{t('hero.hello')} {first}</h1>
        <p className="hero__meta">
          {roleText && <span>{roleText}</span>}
          {institution && <span> · {institution.name}</span>}
        </p>
        {subtitle && <p className="hero__sub">{subtitle}</p>}
      </div>
      <div className="hero__actions">
        {actions}
        <Link to="/me/account" className="btn btn--ghost btn--sm"><UserCog size={16} aria-hidden /> {t('nav.me.account')}</Link>
      </div>
    </section>
  );
}
